import React, { useRef, useEffect, useState } from 'react';
import { Drawer, Button, Space, Typography, Row, Col, Divider, Table } from 'antd';
import { PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { systemService, SystemSummary } from '../../services/systemService';
import { useAuthStore } from '../../stores/authStore';

const { Text } = Typography;

interface StockOutItem {
  id: number;
  medicine_id: number;
  medicine_name: string;
  medicine_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_id: number;
  lot_number: string;
  expiry_date: string;
  total_amount: number;
  notes: string;
  available_stock: number;
}

interface StockOutVoucher {
  id: number;
  voucher_number: string;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  total_amount: number;
  status: 'pending' | 'completed' | 'cancelled';
  notes: string;
  created_at: string;
  completed_at?: string;
  created_by: string;
  created_by_name?: string;
}

interface StockOutPrintData {
  voucher: StockOutVoucher;
  items: StockOutItem[];
}

interface StockOutPrintModalProps {
  visible: boolean;
  onCancel: () => void;
  data: StockOutPrintData | null;
  isLoading?: boolean;
}

const StockOutPrintModal: React.FC<StockOutPrintModalProps> = ({
  visible,
  onCancel,
  data,
  isLoading = false
}) => {
  const [systemInfo, setSystemInfo] = useState<SystemSummary | null>(null);
  const [isLoadingSystemInfo, setIsLoadingSystemInfo] = useState(false);
  const { user } = useAuthStore();

  const companyName = systemInfo?.company?.name || 'CÔNG TY TNHH HOA SINH BIO';
  const companyAddress = systemInfo?.company?.address || 'Số 15 Ngõ 39, Phố Yên Lộ, Phường Yên Nghĩa, TP Hà Nội';
  const companyPhone = systemInfo?.company?.phone || '1900 1234';
  const companyEmail = systemInfo?.company?.email || 'contact@abcpharma.com';
  const companyLogoUrl = '/logo/log-hoasinh.png';

  // Fetch system information when modal opens
  useEffect(() => {
    if (visible && !systemInfo) {
      setIsLoadingSystemInfo(true);
      systemService.getSystemSummary()
        .then((info) => {
          setSystemInfo(info);
        })
        .catch((error) => {
          console.error('Error loading system info:', error);
        })
        .finally(() => {
          setIsLoadingSystemInfo(false);
        });
    }
  }, [visible, systemInfo]);

  // Debug logs
  useEffect(() => {
    if (data && visible) {
      console.log('StockOutPrintModal data:', data);
      console.log('Voucher data:', data.voucher);
      console.log('Voucher number:', data.voucher?.voucher_number);
      console.log('Items data:', data.items);
      console.log('System info:', systemInfo);
      console.log('Items with calculations:');
      data.items?.forEach((item, index) => {
        const total = (item.quantity || 0) * (item.unit_price || 0);
        console.log(`Item ${index + 1}:`, {
          name: item.medicine_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          calculated_total: total,
          original_total_amount: item.total_amount
        });
      });
      const calculatedTotal = data.items?.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_price || 0)), 0) || 0;
      console.log('Calculated total:', calculatedTotal);
      console.log('Voucher total_amount:', data.voucher?.total_amount);
    }
  }, [data, visible, systemInfo]);
  const printRef = useRef<HTMLDivElement>(null);

    const handlePrint = () => {
    console.log('handlePrint called');
    console.log('printRef.current:', printRef.current);
    
    if (printRef.current) {
      // In trực tiếp từ mẫu preview
      const printContent = printRef.current.innerHTML;
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      
      if (printWindow) {
        const printDocument = `
          <!DOCTYPE html>
          <html lang="vi">
          <head>
              <meta charset="UTF-8">
              <meta name="viewport" content="width=device-width, initial-scale=1.0">
              <title>Phiếu Xuất Kho - ${data?.voucher?.voucher_number || 'Unknown'}</title>
              <link rel="preconnect" href="https://fonts.googleapis.com">
              <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
              <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap" rel="stylesheet">
              <style>
                  body {
                      font-family: 'Roboto', sans-serif;
                      background-color: #ffffff;
                      margin: 0;
                      padding: 0;
                  }
                  .print-container {
                      max-width: 800px;
                      margin: 0 auto;
                      background-color: #ffffff;
                      padding: 15px;
                      box-sizing: border-box;
                  }
                  /* CSS cho các phần khác */
                  .ant-table {
                      border: 1px solid #d9d9d9;
                  }
                  .ant-table-thead > tr > th {
                      border: 1px solid #d9d9d9 !important;
                      background-color: #fafafa !important;
                  }
                  .ant-table-tbody > tr > td {
                      border: 1px solid #d9d9d9 !important;
                  }
                  .ant-row {
                      display: flex;
                      flex-wrap: wrap;
                  }
                  .ant-col {
                      flex: 1;
                  }
                  /* CSS cho tỷ lệ cột 60%-40% khi in */
                  .ant-col-14 {
                      width: 60% !important;
                      flex: 0 0 60% !important;
                  }
                  .ant-col-10 {
                      width: 40% !important;
                      flex: 0 0 40% !important;
                  }
                  /* CSS cho phần thông tin khách hàng khi in */
                  .customer-info-section {
                      padding: 16px 0;
                      margin-bottom: 24px;
                  }
                  .customer-info-row {
                      display: flex;
                      margin-bottom: 8px;
                  }
                  .customer-info-row:last-child {
                      margin-bottom: 0;
                  }
                  .customer-info-col-left {
                      width: 60%;
                      padding-right: 12px;
                  }
                  .customer-info-col-right {
                      width: 40%;
                      padding-left: 12px;
                  }
                  .customer-info-label {
                      font-weight: 600;
                      color: #262626;
                  }
                  .customer-info-value {
                      margin-left: 8px;
                      color: #595959;
                  }
                  /* CSS cho bảng khi in */
                  table {
                      width: 100%;
                      border-collapse: collapse;
                      border: 1px solid #d9d9d9;
                  }
                  th, td {
                      border: 1px solid #d9d9d9;
                      padding: 8px 12px;
                      text-align: left;
                      font-size: 12px;
                      vertical-align: top;
                  }
                  th {
                      background-color: #fafafa;
                      font-weight: 600;
                      color: #262626;
                  }
                  /* Đảm bảo border hiển thị khi in */
                  @media print {
                      table, th, td {
                          border: 1px solid #000 !important;
                      }
                      th {
                          background-color: #f0f0f0 !important;
                      }
                  }
                  @media print {
                      body {
                          background-color: #ffffff;
                      }
                      .print-container {
                          box-shadow: none;
                          margin: 0;
                          width: 210mm;
                          min-height: 200mm;
                          border: none;
                          padding: 5mm 5mm 5mm 5mm;
                      }
                      /* Đảm bảo tất cả border hiển thị khi in */
                      table, th, td {
                          border: 1px solid #000 !important;
                          font-size: 12px;
                          padding: 7px;
                      }
                      th {
                          background-color: #f0f0f0 !important;
                      }
                      /* Đảm bảo text hiển thị đúng */
                      * {
                          color: #000 !important;
                      }
                      /* Đảm bảo tỷ lệ cột 60%-40% khi in */
                      .ant-col-14 {
                          width: 60% !important;
                          flex: 0 0 60% !important;
                      }
                      .ant-col-10 {
                          width: 40% !important;
                          flex: 0 0 40% !important;
                      }
                      /* CSS cho phần thông tin khách hàng khi in */
                      .customer-info-section {
                          padding: 16px 0 !important;
                          margin-bottom: 24px !important;
                      }
                      .customer-info-row {
                          display: flex !important;
                          margin-bottom: 8px !important;
                      }
                      .customer-info-row:last-child {
                          margin-bottom: 0 !important;
                      }
                      .customer-info-col-left {
                          width: 60% !important;
                          padding-right: 12px !important;
                      }
                      .customer-info-col-right {
                          width: 40% !important;
                          padding-left: 12px !important;
                      }
                      .customer-info-label {
                          font-weight: 600 !important;
                          color: #000 !important;
                      }
                      .customer-info-value {
                          margin-left: 8px !important;
                          color: #000 !important;
                      }
                  }
              </style>
          </head>
          <body>
              <div class="print-container">
                  ${printContent}
              </div>
          </body>
          </html>
        `;
        
        printWindow.document.write(printDocument);
        printWindow.document.close();
        printWindow.focus();
        
        // Đợi một chút để CSS load xong rồi mới in
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      } else {
        console.error('Không thể mở cửa sổ in');
        alert('Không thể mở cửa sổ in. Vui lòng cho phép popup và thử lại.');
      }
    } else {
      console.error('printRef.current is null');
    }
  };

  const handleDownload = async () => {
    if (!data || !printRef.current) return;
    
    try {
      // Tạo canvas từ nội dung cần in
      const canvas = await html2canvas(printRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // Tạo PDF
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 295; // A4 height in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;
      
      // Thêm trang đầu
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Thêm các trang tiếp theo nếu cần
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Tải xuống PDF
      pdf.save(`Phiếu xuất kho - ${data.voucher.voucher_number}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Có lỗi khi tạo file PDF. Vui lòng thử lại.');
    }
  };

  if (isLoading || isLoadingSystemInfo) {
    return (
      <Drawer
        title="In phiếu xuất kho"
        open={visible}
        onClose={onCancel}
        width={1000}
        destroyOnClose
      >
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div>Đang tải dữ liệu phiếu và thông tin hệ thống...</div>
        </div>
      </Drawer>
    );
  }

  if (!data || !data.voucher || !data.items) return null;

  const totalQuantityByMedicine: Record<string, number> = {};
  const rowCountByMedicine: Record<string, number> = {};
  const firstRowIndexByMedicine: Record<string, number> = {};
  data.items.forEach((item, idx) => {
    if (!totalQuantityByMedicine[item.medicine_code]) {
      totalQuantityByMedicine[item.medicine_code] = 0;
      rowCountByMedicine[item.medicine_code] = 0;
      firstRowIndexByMedicine[item.medicine_code] = idx;
    }
    totalQuantityByMedicine[item.medicine_code] += item.quantity;
    rowCountByMedicine[item.medicine_code]++;
  });

  return (
    <Drawer
      title="In phiếu xuất kho"
      open={visible}
      onClose={onCancel}
      width={1000}
      destroyOnClose
      extra={
        <Space>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownload}
          >
            Tải xuống
          </Button>
          <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={() => {
              console.log('Print button clicked');
              console.log('data:', data);
              console.log('printRef.current:', printRef.current);
              handlePrint();
            }}
          >
            In phiếu
          </Button>
        </Space>
      }
    >
             <div 
         ref={printRef} 
                   style={{ 
            padding: '15px', 
            backgroundColor: 'white',
            maxWidth: '800px',
            margin: '0 auto',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
          }}
       >
        <div style={{ padding: '0 10px' }}>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <img
                src={companyLogoUrl}
                alt={`${companyName} logo`}
                style={{
                  height: '72px',
                  width: '72px',
                  borderRadius: '16px',
                  objectFit: 'cover',
                  border: '1px solid #e2e8f0'
                }}
              />
              <div>
                <Text strong style={{ fontSize: '18px', display: 'block', color: '#1a202c' }}>
                  {companyName}
                </Text>
                <Text style={{ fontSize: '13px', color: '#475569', display: 'block', marginBottom: '2px' }}>
                  <i className="fas fa-map-marker-alt" style={{ marginRight: '6px', color: '#1d4ed8' }} />
                  {companyAddress}
                </Text>
                <Text style={{ fontSize: '13px', color: '#475569', display: 'block' }}>
                  <i className="fas fa-phone-alt" style={{ marginRight: '6px', color: '#1d4ed8' }} />
                  {companyPhone} · {companyEmail}
                </Text>
              </div>
            </div>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
              <Text style={{ fontSize: '22px', color: '#1d4ed8', fontWeight: 600 }}>PHIẾU XUẤT KHO</Text>
              <span style={{
                padding: '6px 16px',
                backgroundColor: '#e0e7ff',
                color: '#1d4ed8',
                borderRadius: '16px',
                fontWeight: 600,
                fontSize: '14px'
              }}>
                Số: {data.voucher.voucher_number}
              </span>
            </div>
          </div>
        </div>

        <div style={{ marginBottom: '30px' }}>
          <Text style={{ fontSize: '14px', fontStyle: 'italic', display: 'block' }}>
            Ngày <span style={{ borderBottom: '1px solid #000', padding: '0 16px' }}>{dayjs(data.voucher.created_at).format('DD')}</span> tháng <span style={{ borderBottom: '1px solid #000', padding: '0 16px' }}>{dayjs(data.voucher.created_at).format('MM')}</span> năm <span style={{ borderBottom: '1px solid #000', padding: '0 16px' }}>{dayjs(data.voucher.created_at).format('YYYY')}</span>
          </Text>
        </div>

        {/* Customer Info */}
        <div className="customer-info-section">
          <div className="customer-info-row">
            <div className="customer-info-col-left">
              <span className="customer-info-label">Họ tên người nhận hàng:</span>
              <span className="customer-info-value">{data.voucher.customer_name || 'Chưa có thông tin'}</span>
            </div>
            <div className="customer-info-col-right">
              <span className="customer-info-label">Điện thoại:</span>
              <span className="customer-info-value">{data.voucher.customer_phone || 'Chưa có thông tin'}</span>
            </div>
          </div>
          <div className="customer-info-row">
            <div className="customer-info-col-left">
              <span className="customer-info-label">Địa chỉ:</span>
              <span className="customer-info-value">{data.voucher.customer_address || 'Chưa có thông tin'}</span>
            </div>
            <div className="customer-info-col-right">
              <span className="customer-info-label">Lý do xuất kho:</span>
              <span className="customer-info-value">Xuất bán hàng theo phiếu xuất kho</span>
            </div>
          </div>
          <div className="customer-info-row">
            <div className="customer-info-col-left">
              <span className="customer-info-label">Trạng thái phiếu:</span>
              <span className="customer-info-value">
                {data.voucher.status === 'completed' ? 'Đã hoàn thành' : 
                 data.voucher.status === 'cancelled' ? 'Đã hủy' : 'Chờ xử lý'}
              </span>
            </div>
            <div className="customer-info-col-right">
              <span className="customer-info-label">Ngày hoàn thành:</span>
              <span className="customer-info-value">
                {data.voucher.completed_at ? dayjs(data.voucher.completed_at).format('DD/MM/YYYY HH:mm') : 'Chưa hoàn thành'}
              </span>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div style={{ marginBottom: '20px' }}>
          <Table
            columns={[
              {
                title: 'STT',
                key: 'index',
                width: 60,
                render: (_: any, __: any, index: number) => index + 1,
              },
              {
                title: 'Tên sản phẩm, thuốc',
                dataIndex: 'medicine_name',
                key: 'medicine_name',
              },
              {
                title: 'Số lô',
                dataIndex: 'lot_number',
                key: 'lot_number',
              },
              {
                title: 'ĐVT',
                dataIndex: 'unit',
                key: 'unit',
              },
              {
                title: 'Số lượng',
                key: 'quantity',
                render: (_: any, record: StockOutItem) => (
                  <div style={{ textAlign: 'right' }}>{record.quantity}</div>
                ),
              },
              {
                title: 'Đơn giá (VNĐ)',
                key: 'unit_price',
                render: (_: any, record: StockOutItem) => (
                  <div style={{ textAlign: 'right' }}>{(record.unit_price || 0).toLocaleString('vi-VN')}</div>
                ),
              },
              {
                title: 'Tổng SL',
                key: 'total_quantity',
                render: (_: any, record: StockOutItem, idx: number) => {
                  const isFirst = firstRowIndexByMedicine[record.medicine_code] === idx;
                  return isFirst ? (
                    <div style={{ textAlign: 'right' }}>{totalQuantityByMedicine[record.medicine_code]}</div>
                  ) : null;
                },
                onCell: (record: StockOutItem, rowIndex: number | undefined) => {
                  if (typeof rowIndex !== 'number') return {};
                  const isFirst = firstRowIndexByMedicine[record.medicine_code] === rowIndex;
                  return isFirst
                    ? { rowSpan: rowCountByMedicine[record.medicine_code] }
                    : { rowSpan: 0 };
                },
              },
              {
                title: 'Thành tiền (VNĐ)',
                key: 'total_amount',
                render: (_: any, record: StockOutItem) => (
                  <div style={{ textAlign: 'right', fontWeight: 500 }}>
                    {((record.quantity || 0) * (record.unit_price || 0)).toLocaleString('vi-VN')}
                  </div>
                ),
              },
            ]}
            dataSource={data.items}
            rowKey="id"
            pagination={false}
            size="small"
            bordered
            style={{ fontSize: '12px' }}
          />
        </div>

        {/* Summary */}
        <div style={{ textAlign: 'right', marginBottom: '20px' }}>
          <div style={{ width: '50%', marginLeft: 'auto' }}>
            <Row justify="space-between" style={{ marginBottom: '8px' }}>
              <Col>Cộng thành tiền:</Col>
              <Col>{(data.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_price || 0)), 0) || 0).toLocaleString('vi-VN')} VNĐ</Col>
            </Row>
            <Row justify="space-between">
              <Col><Text strong style={{ fontSize: '16px' }}>THANH TOÁN:</Text></Col>
              <Col><Text strong style={{ fontSize: '16px', color: '#005A9C' }}>
                {(data.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_price || 0)), 0) || 0).toLocaleString('vi-VN')} VNĐ
              </Text></Col>
            </Row>
          </div>
          
          {/* Bằng chữ */}
          <div style={{ textAlign: 'left', marginTop: '16px' }}>
            <Text>
              <strong>Bằng chữ:</strong> <em style={{ fontStyle: 'italic' }}>
                {(() => {
                  const subtotal = data.items.reduce((sum, item) => sum + ((item.quantity || 0) * (item.unit_price || 0)), 0) || 0;
                  
                  // Hàm chuyển số thành chữ
                  const numberToWords = (num: number): string => {
                    const ones = ['', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
                    const teens = ['mười', 'mười một', 'mười hai', 'mười ba', 'mười bốn', 'mười lăm', 'mười sáu', 'mười bảy', 'mười tám', 'mười chín'];
                    const tens = ['', '', 'hai mươi', 'ba mươi', 'bốn mươi', 'năm mươi', 'sáu mươi', 'bảy mươi', 'tám mươi', 'chín mươi'];
                    
                    if (num === 0) return 'không';
                    if (num < 10) return ones[num];
                    if (num < 20) return teens[num - 10];
                    if (num < 100) {
                      const ten = Math.floor(num / 10);
                      const one = num % 10;
                      if (one === 0) return tens[ten];
                      if (one === 1) return tens[ten] + ' mốt';
                      return tens[ten] + ' ' + ones[one];
                    }
                    if (num < 1000) {
                      const hundred = Math.floor(num / 100);
                      const remainder = num % 100;
                      if (remainder === 0) return ones[hundred] + ' trăm';
                      return ones[hundred] + ' trăm ' + numberToWords(remainder);
                    }
                    if (num < 1000000) {
                      const thousand = Math.floor(num / 1000);
                      const remainder = num % 1000;
                      if (remainder === 0) return numberToWords(thousand) + ' nghìn';
                      return numberToWords(thousand) + ' nghìn ' + numberToWords(remainder);
                    }
                    if (num < 1000000000) {
                      const million = Math.floor(num / 1000000);
                      const remainder = num % 1000000;
                      if (remainder === 0) return numberToWords(million) + ' triệu';
                      return numberToWords(million) + ' triệu ' + numberToWords(remainder);
                    }
                    return 'số quá lớn';
                  };
                  
                  return numberToWords(Math.floor(subtotal)) + ' đồng.';
                })()}
              </em>
            </Text>
          </div>
        </div>

        {/* Notes */}
        {data.voucher.notes && (
          <div style={{ marginBottom: '20px' }}>
            <Text strong>Ghi chú:</Text>
            <div style={{ 
              marginTop: '8px', 
              padding: '12px', 
              backgroundColor: '#f9f9f9', 
              borderRadius: '4px',
              borderLeft: '4px solid #005A9C'
            }}>
              {data.voucher.notes}
            </div>
          </div>
        )}

        {/* Footer */}
        <Divider />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginTop: '40px',
          textAlign: 'center'
        }}>
          <div style={{ flex: 1, margin: '0 8px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Người lập phiếu</Text>
            </div>
            <div style={{ marginBottom: '0px', fontStyle: 'italic', fontSize: '13px', color: '#718096' }}>
              (Ký và ghi rõ họ tên)
            </div>
            <div style={{ paddingTop: '40px', minHeight: '20px', fontStyle: 'italic', fontSize: '13px', color: '#718096' }}>
              {data.voucher.created_by_name || data.voucher.created_by || user?.full_name || user?.username || '(Ký, họ tên)'}
            </div>
          </div>
          <div style={{ flex: 1, margin: '0 8px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Người nhận hàng</Text>
            </div>
            <div style={{ marginBottom: '0px', fontStyle: 'italic', fontSize: '13px', color: '#718096' }}>
              (Ký và ghi rõ họ tên)
            </div>
            <div style={{ paddingTop: '40px', minHeight: '20px', fontStyle: 'italic', fontSize: '13px', color: '#718096' }}>
              {data.voucher.customer_name || '(Ký, họ tên)'}
            </div>
          </div>
          <div style={{ flex: 1, margin: '0 8px', wordWrap: 'break-word', overflowWrap: 'break-word' }}>
            <div style={{ marginBottom: '8px' }}>
              <Text strong>Kế toán trưởng</Text>
            </div>
            <div style={{ marginBottom: '0px', fontStyle: 'italic', fontSize: '13px', color: '#718096' }}>
              (Ký và ghi rõ họ tên)
            </div>
          </div>
        </div>
         </div>
       </div>
     </Drawer>
  );
};

export default StockOutPrintModal; 